import { PointCloudManager, StreamablePointCloud, StreamablePointCloudNode } from '.';
import { readFileBuffer, readFileJSON} from "../file-system-loader";
import LocalScene from '../projections';
import { Vector3 } from 'three';
import { AsyncWorkerPool } from '../worker-pool';

type NodeArguments = ConstructorParameters<typeof EncompassPointCloudNode>

const workerPool = new AsyncWorkerPool("point-decoder-encompass.js");

/** Encompass style pointcloud node */
class EncompassPointCloudNode extends StreamablePointCloudNode {
  public cloud: EncompassPointCloud;
  public parent: EncompassPointCloudNode;

  constructor(cloud, parent, name, level, center, size,) {
    super(name, level, center, size);

    this.cloud = cloud;
    this.parent = parent;
  }

  get precision() {
    return this.cloud.precision;
  }

  get hierarchy() {
    return this.cloud.hierarchy;
  }

  getChildOffset(index) {
    return {
      x: index & 0b001,
      y: (index & 0b010) / 2,
      z: (index & 0b100) / 4
    };
  }

  async readNodeHierarchy() {
    console.warn("Not implemented");
  }

  async getBinaryData() {
    let dataPath = this.cloud.getDataPath(this.name);
    let nodePath = `${dataPath}/${this.name}`;

    let minCorner = new Vector3()
      .add(this.nodeCenter)
      .subScalar(this.size/2);

    // Read node data
    const buffer = await readFileBuffer(nodePath);

    const values = {
      buffer: buffer,
      name: this.name,
      precision: this.precision,
      size: this.size,
      corner: minCorner
    };

    const data = await workerPool.postMessage(values, [values.buffer]);

    // Convert array to vector
    data.xyz_min = new Vector3(...data.xyz_min);
    data.xyz_max = new Vector3(...data.xyz_max);

    // Convert raw buffer to float array
    data.positions = new Float32Array(data.positions);
    data.intensity = new Float32Array(data.intensity);
    data.classification = new Float32Array(data.classification);
    data.colors = new Float32Array(data.colors);

    return data;
  }

  getGeometry(data) {
    this.setGeometry(data);

    this.numPoints = data.num_points;
    this.density = data.density;

    // Update unique values and min/max values
    this.cloud.updateClassifications(data.classification);
    this.cloud.updateColorLimits(data.rgb_max);
    this.cloud.updateIntensityRange(data.intensity_min, data.intensity_max);

    this.cloud.updateBoundingBox({
      min: data.xyz_min,
      max: data.xyz_max
    });

    this.loaded = true;
    this.loading = false;
  }

  async load() {
    if ((this.loaded) || (this.loading)) return;

    this.loading = true;

    await this.readNodeHierarchy();

    try {
      const data = await this.getBinaryData();
      this.getGeometry(data);
    } catch(err) {
      console.error(err);
      if (this.level !== 1) {
        this.parent.removeChildNode(this);
      }
    }
  }
}

/** Encompass cloud node with no hierarchy */
class EncompassPointCloudNodeV0 extends EncompassPointCloudNode {
  constructor (...args: NodeArguments) {
    super(...args);
  }

  async readNodeHierarchy() {
    // No hierarchy is provided so we assume all 8 children exist
    // Missing nodes will be deleted later if the data doesnt exist
    for (let childIndex = 0; childIndex < 8; childIndex++) {
      let child = this.getChildNode(childIndex);
      this.addChildNode(child);
    }
  }
}

/** Encompass cloud node hierarchy inside r.idx */
class EncompassPointCloudNodeV1 extends EncompassPointCloudNode {
  constructor (...args: NodeArguments) {
    super(...args);
  }

  async readNodeHierarchy() {
    // Read legacy hierarchy
    for (let childIndex = 0; childIndex < 8; childIndex++) {
      let childName = `${this.name}${childIndex}`;
      let childExists = this.hierarchy[childName] !== undefined;
      if (!childExists) continue;
      let child = this.getChildNode(childIndex);
      this.addChildNode(child);
    }
  }
}

/** Encompass cloud node with folder based hierarchy */
class EncompassPointCloudNodeV2 extends EncompassPointCloudNode {
  constructor (...args: NodeArguments) {
    super(...args);
  }

  get isFolderRoot() {
    const nodeLength = (this.name.length - 1);
    return nodeLength % this.cloud.folderDepth === 0;
  }

  async loadHierarchy() {
    let dataPath = this.cloud.getDataPath(this.name);
    let hrcPath = `${dataPath}/${this.name}.hrc`;

    try {
      // Read hierarchy data
      const hierarchy = await readFileJSON(hrcPath);
      this.cloud.updateHierarchy(hierarchy);
    } catch {
      console.log(`Error reading hierarchy: ${this.name}`);
    }
  }

  async readNodeHierarchy() {
    if (this.isFolderRoot) {
      await this.loadHierarchy();
    }

    // Update children with known hierarchy
    const indices = this.hierarchy[this.name];
    indices?.forEach(childIndex => {
      let child = this.getChildNode(childIndex);
      this.addChildNode(child);
    });
  }
}

/** Encompass style pointcloud */
export class EncompassPointCloud extends StreamablePointCloud {
  public precision;
  public folderDepth;
  public hierarchy = {};

  constructor(pointclouds: PointCloudManager) {
    super(pointclouds);
  }

  get metadataPath() {
    return `${this.structure.path}/r.idx`;
  }

  getDataPath(node) {
    let dataDir = this.dataDir;
    let folderDepth = this.folderDepth;

    if (node === "r") {
      return dataDir;
    }

    let name = node.slice(1);
    if ((folderDepth) && (name.length >= folderDepth)) {
      let dirs = name.match(new RegExp('.{' + folderDepth + '}', 'g'));
      dataDir = `${dataDir}/${dirs.join("/")}`;
    }

    return dataDir;
  }

  /** Decode cloud metadata. Cloud is already converted to meters */
  decodeMetadata(data) {
    this.precision = data.hasOwnProperty('precision')
      ? data.precision
      : 0.005;

    this.folderDepth = data.hasOwnProperty('hierarchy_step_size')
      ? data.hierarchy_step_size
      : null;

    this.spacing = data.hasOwnProperty('spacing')
      ? data.spacing
      : Math.sqrt(2) * data.size / 250.0;

    const offset = new Vector3(...data.point_offset);

    // Get our tight bounding box if available
    let tightBoundingBox = null;
    if ("tight_bounding_box" in data) {
      let box = data.tight_bounding_box;
      tightBoundingBox = {
        min: new Vector3(...box.min).sub(offset),
        max: new Vector3(...box.max).sub(offset)
      };
    }

    // Generate cloud root node
    let nodeSize = parseFloat(data.size);
    let nodeCenter = new Vector3(...data.center)
      .sub(offset);

    this.shift = LocalScene.offsetToScene(offset);

    const hasHierarchyV1 = this.checkLegacyHierarchy(data);
    const hasHierarchyV2 = data.hasOwnProperty('contains_hierarchy')
      ? data.contains_hierarchy
      : false;

    let RootNodeConstructor;
    if (hasHierarchyV1) {
      RootNodeConstructor = EncompassPointCloudNodeV1;
    } else if (hasHierarchyV2) {
      RootNodeConstructor = EncompassPointCloudNodeV2;
    } else {
      RootNodeConstructor = EncompassPointCloudNodeV0;
    }

    const rootNode = new RootNodeConstructor(
      this, null, "r", 1, nodeCenter, nodeSize);

    this.root = rootNode;
    this.updateBoundingBox(tightBoundingBox);
    this.pointclouds.updateMinimapExtent();

    if (this.saved) return;
    this.viewer.zoomToSceneExtent(this.tightBoundingBox, false);
  }

  updateHierarchy(hierarchy) {
    this.hierarchy = {
      ...this.hierarchy,
      ...hierarchy
    };
  }

  /** Check for legacy style hierarchy */
  checkLegacyHierarchy(data) {
    const availableNodes = data.hasOwnProperty('node_structure')
      ? data.node_structure
      : null;

    if (availableNodes && availableNodes.r) {
      console.log("Legacy hierarchy detected");
      this.updateHierarchy(availableNodes);
      return true;
    }

    return false;
  }
}
